import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import type { OutputContentType } from "@offsite/shared";

/**
 * Dauerhafter, persönlicher Ergebnis-Link (`/ergebnis/:resultId`) —
 * öffentlich, ohne Auth-Gate: die resultId selbst ist die Berechtigung
 * (unguessable UUID), siehe API-Kontrakt `GET /ergebnis/{resultId}`.
 *
 * Zustände:
 * - pending  → Wartetext, pollt alle 5s weiter
 * - complete → Render wie OutputView (sandboxed iframe für html, pre sonst)
 * - error/404 → klare Meldung, an die Moderation verweisen
 */

type ErgebnisState =
  | { kind: "loading" }
  | { kind: "pending" }
  | {
      kind: "complete";
      name: string;
      thema: string;
      contentType?: OutputContentType;
      content?: string;
    }
  | { kind: "error" }
  | { kind: "not-found" };

export function ErgebnisPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const [state, setState] = useState<ErgebnisState>({ kind: "loading" });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!resultId) {
      setState({ kind: "not-found" });
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const r = await api.ergebnis.get(resultId);
        if (cancelled) return;
        if (r.status === "pending") {
          setState({ kind: "pending" });
          timer.current = setTimeout(load, 5000);
        } else if (r.status === "complete") {
          setState({
            kind: "complete",
            name: r.name,
            thema: r.thema,
            contentType: r.contentType,
            content: r.content,
          });
        } else {
          setState({ kind: "error" });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "not-found" });
        } else {
          setState({ kind: "error" });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [resultId]);

  return (
    <main
      style={{
        minHeight: "100vh",
        background: "var(--paper-50, #fafafa)",
        color: "var(--paper-900, #161b21)",
        padding: "var(--s-7, 2rem) var(--s-5, 1.25rem)",
        display: "grid",
        gap: "var(--s-5, 1.25rem)",
      }}
    >
      {state.kind === "loading" && (
        <p className="deck-sub" role="status">
          Lädt …
        </p>
      )}

      {state.kind === "pending" && (
        <div style={{ display: "grid", gap: 8, textAlign: "center", padding: "3rem 0" }}>
          <div className="deck-eyebrow">Wird gerade gebaut</div>
          <p className="deck-sub" role="status" data-testid="ergebnis-pending">
            Dein Prototyp entsteht gerade — schau in ein paar Minuten wieder
            vorbei. Diese Seite aktualisiert sich von selbst.
          </p>
        </div>
      )}

      {state.kind === "not-found" && (
        <div style={{ display: "grid", gap: 8, textAlign: "center", padding: "3rem 0" }}>
          <div className="deck-eyebrow">Nicht gefunden</div>
          <p className="deck-sub" role="alert" data-testid="ergebnis-not-found">
            Diesen Link kennen wir nicht. Frag die Moderation.
          </p>
        </div>
      )}

      {state.kind === "error" && (
        <div style={{ display: "grid", gap: 8, textAlign: "center", padding: "3rem 0" }}>
          <div className="deck-eyebrow">Etwas ist schiefgelaufen</div>
          <p className="deck-sub" role="alert" data-testid="ergebnis-error">
            Dein Ergebnis konnte nicht gebaut werden. Frag die Moderation.
          </p>
        </div>
      )}

      {state.kind === "complete" && (
        <section style={{ display: "grid", gap: 14 }}>
          <header>
            <div className="deck-eyebrow">{state.name}</div>
            <h1
              className="deck-title"
              style={{ fontSize: "clamp(1.4rem, 4vw, 2rem)", margin: "4px 0 0" }}
            >
              {state.thema}
            </h1>
          </header>
          {state.contentType === "html" ? (
            // sandbox="allow-scripts" only — kein allow-same-origin, keine
            // Forms, keine Popups, keine Top-Navigation (siehe OutputView /
            // OutputRenderer für die volle Sandbox-Begründung).
            <iframe
              title={`Ergebnis — ${state.name}`}
              srcDoc={state.content}
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              loading="lazy"
              className="output-frame"
            />
          ) : (
            <pre className="output-pre">{state.content}</pre>
          )}
        </section>
      )}
    </main>
  );
}
